import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigation } from '../contexts/NavigationContext';
import LanguageDropdown from './LanguageDropdown';
import { 
  IconHome, 
  IconMenu, 
  IconX, 
  IconChevronDown,
  IconMapPin,
  IconUsers,
  IconFileText,
  IconDollarSign,
  IconWrench,
  IconMessageSquare,
  IconBot,
  IconMail,
  IconPalette
} from './Icons';

const Header = () => {
  const { t } = useLanguage();
  const { navigateTo, dropdownStates, toggleDropdown, closeAllDropdowns } = useNavigation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleNavigate = (view) => {
    navigateTo(view);
    setIsMobileMenuOpen(false);
  };

  const platformFeatures = [
    { id: 'propertyPromotion', icon: IconMapPin },
    { id: 'tenantScreening', icon: IconUsers },
    { id: 'eContract', icon: IconFileText },
    { id: 'rentManagement', icon: IconDollarSign },
    { id: 'maintenance', icon: IconWrench },
    { id: 'communicationSupport', icon: IconMessageSquare },
  ];

  const professionalServices = [
    { id: 'technicianDirectory', icon: IconWrench },
    { id: 'interiorDesignService', icon: IconPalette },
  ];

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div 
            className="flex items-center cursor-pointer"
            onClick={() => handleNavigate('home')}
          >
            <IconHome className="w-8 h-8 text-blue-600 mr-3" />
            <div>
              <h1 className="text-xl font-bold text-gray-900">{t('appTitle')}</h1>
              <p className="text-xs text-gray-600">{t('appSubtitle')}</p>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6">
            <button
              onClick={() => handleNavigate('home')}
              className="text-gray-700 hover:text-blue-600 px-3 py-2 text-sm font-medium"
            >
              {t('overview')}
            </button>

            {/* Platform Features Dropdown */}
            <div className="relative">
              <button
                onClick={() => toggleDropdown('platformFeatures')}
                className="flex items-center text-gray-700 hover:text-blue-600 px-3 py-2 text-sm font-medium"
              >
                {t('platformFeatures')}
                <IconChevronDown className={`ml-1 h-4 w-4 transition-transform ${dropdownStates.platformFeatures ? 'rotate-180' : ''}`} />
              </button>
              {dropdownStates.platformFeatures && (
                <div className="absolute left-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5">
                  <div className="py-1">
                    {platformFeatures.map(({ id, icon: Icon }) => (
                      <button
                        key={id}
                        onClick={() => handleNavigate(id)}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <Icon className="w-4 h-4 mr-3" />
                        {t(id)}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Professional Services Dropdown */}
            <div className="relative">
              <button
                onClick={() => toggleDropdown('professionalServices')}
                className="flex items-center text-gray-700 hover:text-blue-600 px-3 py-2 text-sm font-medium"
              >
                {t('professionalServices')}
                <IconChevronDown className={`ml-1 h-4 w-4 transition-transform ${dropdownStates.professionalServices ? 'rotate-180' : ''}`} />
              </button>
              {dropdownStates.professionalServices && (
                <div className="absolute left-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5">
                  <div className="py-1">
                    {professionalServices.map(({ id, icon: Icon }) => (
                      <button
                        key={id}
                        onClick={() => handleNavigate(id)}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <Icon className="w-4 h-4 mr-3" />
                        {t(id)}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={() => handleNavigate('chatbot')}
              className="flex items-center text-gray-700 hover:text-blue-600 px-3 py-2 text-sm font-medium"
            >
              <IconBot className="w-4 h-4 mr-1" />
              {t('aiAssistant')}
            </button>

            <button
              onClick={() => handleNavigate('contact')}
              className="flex items-center text-gray-700 hover:text-blue-600 px-3 py-2 text-sm font-medium"
            >
              <IconMail className="w-4 h-4 mr-1" />
              {t('contact')}
            </button>

            <LanguageDropdown />
          </nav>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center space-x-4">
            <LanguageDropdown />
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="text-gray-700 hover:text-blue-600 p-2"
            >
              {isMobileMenuOpen ? <IconX /> : <IconMenu />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <div className="md:hidden fixed inset-0 top-16 bg-white z-40 overflow-y-auto">
          <nav className="px-4 pt-2 pb-4 space-y-2">
            <button
              onClick={() => handleNavigate('home')}
              className="block w-full text-left px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-100 rounded-md"
            >
              {t('overview')}
            </button>

            {/* Platform Features */}
            <div>
              <button
                onClick={() => toggleDropdown('platformFeatures')}
                className="flex items-center justify-between w-full px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-100 rounded-md"
              >
                {t('platformFeatures')}
                <IconChevronDown className={`h-4 w-4 transition-transform ${dropdownStates.platformFeatures ? 'rotate-180' : ''}`} />
              </button>
              {dropdownStates.platformFeatures && (
                <div className="mt-2 ml-4 space-y-1">
                  {platformFeatures.map(({ id, icon: Icon }) => (
                    <button
                      key={id}
                      onClick={() => handleNavigate(id)}
                      className="flex items-center w-full px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-md"
                    >
                      <Icon className="w-4 h-4 mr-3" />
                      {t(id)}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Professional Services */}
            <div>
              <button
                onClick={() => toggleDropdown('professionalServices')}
                className="flex items-center justify-between w-full px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-100 rounded-md"
              >
                {t('professionalServices')}
                <IconChevronDown className={`h-4 w-4 transition-transform ${dropdownStates.professionalServices ? 'rotate-180' : ''}`} />
              </button>
              {dropdownStates.professionalServices && (
                <div className="mt-2 ml-4 space-y-1">
                  {professionalServices.map(({ id, icon: Icon }) => (
                    <button
                      key={id}
                      onClick={() => handleNavigate(id)}
                      className="flex items-center w-full px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-md"
                    >
                      <Icon className="w-4 h-4 mr-3" />
                      {t(id)}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={() => handleNavigate('chatbot')}
              className="flex items-center w-full px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-100 rounded-md"
            >
              <IconBot className="w-4 h-4 mr-2" />
              {t('aiAssistant')}
            </button>

            <button
              onClick={() => handleNavigate('contact')}
              className="flex items-center w-full px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-100 rounded-md"
            >
              <IconMail className="w-4 h-4 mr-2" />
              {t('contact')}
            </button>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
